import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  update_address,
  create_address,
  get_zipcode,
} from './graphql/alterAddresses';
import { Apollo } from 'apollo-angular';
import { EnvironmentService } from '../../../environment/environment.service';
import { Router } from '@angular/router';
import { AlertService } from '../../../alert/alert.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { CurrentUserService } from '../../../services/current-user.service';

const UPDATE_ADDRESS = update_address;
const CREATE_ADDRESS = create_address;
const GET_ZIPCODE = get_zipcode;

@Component({
    selector: 'deal-addresses',
    templateUrl: './addresses.component.html',
    styleUrls: ['./addresses.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressesComponent implements OnInit {
  @ViewChild('form') form: ElementRef;

  @Input() address: any;
  @Input() entityID: string;
  @Output() addressChange: EventEmitter<any> = new EventEmitter();

  addressForm: any;
  zipCodeID: string;
  isVerified: boolean;
  addressEditMode: Boolean = true;

  addressSearch: FormControl = new FormControl('');
  typeSubscription: Subscription = new Subscription();

  constructor(
    private apollo: Apollo,
    private env: EnvironmentService,
    private router: Router,
    private renderer: Renderer2,
    private alertService: AlertService,
    private _snackBar: MatSnackBar,
    public userService: CurrentUserService
  ) {}

  ngOnInit() {
    if (this.address) {
      this.isVerified = this.address.is_verified;
      this.zipCodeID = this.address.zipCode.id;
      this.setAddressForm(false);
    } else {
      this.isVerified = false;
      this.zipCodeID = '';
      this.setAddressForm(true);
    }
  }

  passAddress() {
    this.addressChange.emit(this.address);
  }

  openSnackBar() {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open('Data saved successfully.', 'Ok', config);
  }

  setAddressForm(isNew: boolean) {
    this.addressForm = new FormGroup({
      addressSearch: new FormControl(''),
      address1: new FormControl(!isNew ? this.address.address1 : ''),
      address2: new FormControl(!isNew ? this.address.address2 : ''),
      city: new FormControl(
        !isNew ? this.address.zipCode.city : '',
        Validators.required
      ),
      state: new FormControl(
        !isNew ? this.address.zipCode.state.abbreviation : '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(2),
          Validators.pattern(
            '^((A[LKSZR])|(C[AOT])|(D[EC])|(F[ML])|(G[AU])|(HI)|(I[DLNA])|(K[SY])|(LA)|(M[EHDAINSOT])|(N[EVHJMYCD])|(MP)|(O[HKR])|(P[WAR])|(RI)|(S[CD])|(T[NX])|(UT)|(V[TIA])|(W[AVIY]))$'
          ),
        ]
      ),
      zipcode: new FormControl(
        !isNew ? this.address.zipCode.zipcode : '',
        Validators.pattern('(^\\d{5}$)')
      ),
      county: new FormControl(!isNew ? this.address.zipCode.county.name : ''),
      lat: new FormControl(!isNew ? this.address.latitude : '', [
        Validators.pattern('^(\\-)?[0-9]{1,2}(\\.([0-9]{1,14}))?$'),
        Validators.maxLength(19),
        Validators.min(-90),
        Validators.max(90),
      ]),
      long: new FormControl(!isNew ? this.address.longitude : '', [
        Validators.pattern('^(\\-)?[0-9]{1,3}(\\.([0-9]{1,14}))?$'),
        Validators.maxLength(19),
        Validators.min(-180),
        Validators.max(180),
      ]),
      // Only populated by VerifyAddress; not loaded with the deal, so it's only sent after a Google verify
      google_place_data: new FormControl(null),
    });
  }

  checkFormError(): boolean {
    let error = false;
    Object.keys(this.addressForm.controls).forEach((key) => {
      const controlErrors: ValidationErrors = this.addressForm.get(key).errors;
      if (controlErrors != null) {
        error = true;
      }
    });
    return error;
  }

  public VerifyAddress(address: any) {
    if (address.address_components.length > 0) {
      this.isVerified = true;
      this.addressForm.controls['address1'].setValue(address.name);
      this.addressForm.controls['lat'].setValue(
        address.geometry.location.lat()
      );
      this.addressForm.controls['long'].setValue(
        address.geometry.location.lng()
      );
      this.addressForm.controls['google_place_data'].setValue(
        JSON.stringify({
          address_components: address.address_components,
          place_id: address.place_id,
          url: address.url,
          types: address.types,
        })
      );

      let street = '';
      let route = '';
      let address2 = '';
      let city = '';
      let state = '';
      let zipcode = '';
      let county = '';

      for (let i = 0; i < address.address_components.length; i++) {
        if (address.address_components[i]['types'].includes('street_number')) {
          street = address.address_components[i]['long_name'];
        } else if (address.address_components[i]['types'].includes('route')) {
          route = address.address_components[i]['long_name'];
        } else if (
          address.address_components[i]['types'].includes('subpremise')
        ) {
          address2 = address.address_components[i]['long_name'];
        } else if (
          address.address_components[i]['types'].includes('locality')
        ) {
          city = address.address_components[i]['long_name'];
        } else if (
          address.address_components[i]['types'].includes('sublocality') && city == ''
        ) {
          city = address.address_components[i]['long_name'];
        } else if (
          address.address_components[i]['types'].includes(
            'administrative_area_level_1'
          )
        ) {
          state = address.address_components[i]['short_name'];
        } else if (
          address.address_components[i]['types'].includes('postal_code')
        ) {
          zipcode = address.address_components[i]['long_name'];
        } else if (
          address.address_components[i]['types'].includes(
            'administrative_area_level_2'
          )
        ) {
          county = address.address_components[i]['long_name'];
        }
      }

      if (street && route) {
        this.addressForm.controls['address1'].setValue(street + ' ' + route);
      } else if (route) {
        this.addressForm.controls['address1'].setValue(route);
      } else if (street) {
        this.addressForm.controls['address1'].setValue(street);
      } else {
        this.addressForm.controls['address1'].setValue('');
      }

      address2
        ? this.addressForm.controls['address2'].setValue(address2)
        : this.addressForm.controls['address2'].setValue('');
      city
        ? this.addressForm.controls['city'].setValue(city)
        : this.addressForm.controls['city'].setValue('');
      state
        ? this.addressForm.controls['state'].setValue(state)
        : this.addressForm.controls['state'].setValue('');
      zipcode
        ? this.addressForm.controls['zipcode'].setValue(zipcode)
        : this.addressForm.controls['zipcode'].setValue('');

      if (county) {
        county = county.replace(' County', '');
        county = county.replace(' Parish', '');
        county = county.replace(' Borough', '');
        county = county.replace(' Municipality', '');
        this.addressForm.controls['county'].setValue(county);
      } else {
        this.addressForm.controls['county'].setValue('');
      }
    }
  }

  saveAddress(address: any) {
    if (!this.checkFormError()) {
      //If anything about the address has changed, update it to the API.
      if (
        !this.address ||
        this.address.address1 !== address.address1 ||
        this.address.address2 !== address.address2 ||
        this.address.latitude !== address.lat ||
        this.address.longitude !== address.long ||
        this.address.zipCode.zipcode !== address.zipcode ||
        this.address.zipCode.city !== address.city ||
        this.address.zipCode.state.abbreviation !== address.state ||
        this.address.zipCode.county.name !== address.county ||
        this.address.is_verified !== this.isVerified
      ) {
        //If ZipCode related info has changed, fetch a few zipcode id to update address to
        if (
          !this.address ||
          this.address.zipCode.zipcode !== address.zipcode ||
          this.address.zipCode.city !== address.city ||
          this.address.zipCode.state.abbreviation !== address.state ||
          this.address.zipCode.county.name !== address.county
        ) {
          this.apollo
            .watchQuery<any>({
              query: GET_ZIPCODE,
              variables: {
                zipcode: address.zipcode,
                city: address.city,
                state: address.state,
                county: address.county,
              },
            })
            .valueChanges.subscribe(({data}) => {
            let foundZipCode = true;
            if (data.zipCodeWithCityStateAndCounty !== null) {
              this.zipCodeID = data.zipCodeWithCityStateAndCounty.id;
            } else if (
              data.zipCodeWithCityState !== null &&
              data.zipCodeWithCityState.length > 0
            ) {
              this.zipCodeID = data.zipCodeWithCityState[0].id;
            } else if (
              data.zipCodeWithCity &&
              data.zipCodeWithCity.length > 0
            ) {
              this.zipCodeID = data.zipCodeWithCity[0].id;
            } else if (data.zipCode && data.zipCode.length > 0) {
              this.zipCodeID = data.zipCode[0].id;
            } else if (
              data.noZipCityState &&
              data.noZipCityState.length > 0
            ) {
              this.zipCodeID = data.noZipCityState[0].id;
            } else {
              foundZipCode = false;
            }

            if (foundZipCode) {
              this.updateAddress(address);
            } else {
              this.alertService.add(
                '',
                `Failed to update address: Please enter a valid City/State or Zip Code and try again.`
              );
            }
          });
        } else {
          this.updateAddress(address);
        }
      } else {
        this.addressEditMode = true;
      }
    }
  }

  updateAddress(address: any) {
    if (this.address) {
      let graphvar: any = {
        address1: address.address1,
        address2: address.address2,
        latitude: parseFloat(address.lat),
        longitude: parseFloat(address.long),
        is_verified: this.isVerified,
        zip_code_id: this.zipCodeID,
      };
      if (address.google_place_data) {
        graphvar.google_place_data = address.google_place_data;
      }

      this.apollo
        .mutate({
          mutation: UPDATE_ADDRESS,
          variables: {
            id: this.address.id,
            input: graphvar,
          },
        })
        .subscribe(({ data }) => {
          // @ts-ignore
          this.address = data.updateAddress;
          this.passAddress();
          this.setAddressForm(false);
          this.openSnackBar();
          this.addressEditMode = true;
        });
    } else {
      let graphvar: any = {
        address1: address.address1,
        address2: address.address2,
        latitude: parseFloat(address.lat),
        longitude: parseFloat(address.long),
        is_verified: this.isVerified,
        zip_code_id: this.zipCodeID,
        entity_addresses: {
          create: [{ entity_id: this.entityID }],
        },
      };
      if (address.google_place_data) {
        graphvar.google_place_data = address.google_place_data;
      }

      this.apollo
        .mutate({
          mutation: CREATE_ADDRESS,
          variables: {
            input: graphvar,
          },
        })
        .subscribe(({ data }) => {
          // @ts-ignore
          this.address = data.createAddress;
          this.passAddress();
          this.setAddressForm(false);
          this.openSnackBar();
          this.addressEditMode = true;
        });
    }
  }
}
